import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { initials } from '../core/format';
import { ApiError } from '../models/common';
import type { Role, User } from '../models/user';
import { ROLE_DESCRIPTIONS, ROLE_ICONS, ROLE_LABELS } from '../models/user';
import { session } from '../services/auth-service';
import { confirmDialog } from '../services/confirm-service';
import { showToast } from '../services/toast-service';
import { generatePassword, users as userService } from '../services/user-service';

/** Yetki matrisi — tek doğruluk kaynağı deriveClaims/backend; burada sergilenir. */
const MATRIX: Array<[string, boolean, boolean, boolean]> = [
  ['Cihaz listesi & detay', true, true, true],
  ['Kimlik düzenleme', true, true, false],
  ['Yeniden başlat · kurtarma', true, true, false],
  ['Gömülü SSH terminali', true, true, false],
  ['Force install', true, true, false],
  ['İmaj profili atama', true, true, false],
  ['Marka & imaj tanımı', true, false, false],
  ['Kullanıcı & rol yönetimi', true, false, false],
];

const ROLE_CHIP: Record<Role, { bg: string; fg: string }> = {
  admin: { bg: 'var(--vx-brand-50)', fg: 'var(--vx-brand-700)' },
  operator: { bg: 'var(--vx-info-bg)', fg: 'var(--vx-info-text)' },
  viewer: { bg: 'var(--vx-chip-bg)', fg: 'var(--vx-chip-fg)' },
};

const ROLES: Role[] = ['admin', 'operator', 'viewer'];

interface CreateForm {
  username: string;
  fullName: string;
  email: string;
  role: Role;
  password: string;
}

/** Kullanıcı & rol yönetimi: liste + ekleme + rol atama + şifre sıfırlama + silme. */
export class UsersView extends SmgElement {
  private list: User[] = [];
  private loading = true;
  private error = '';

  private createOpen = false;
  private createForm: CreateForm = this.emptyForm();
  private roleMenuFor: string | null = null;
  /** Şifre sıfırlama diyaloğu: hedef + üretilen parola + 'done' aşaması */
  private pwTarget: string | null = null;
  private pwValue = '';
  private pwDone = false;

  private emptyForm(): CreateForm {
    return { username: '', fullName: '', email: '', role: 'operator', password: generatePassword() };
  }

  protected override onConnected(): void {
    this.wireActs();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    this.render();
    try {
      this.list = await userService.list();
    } catch {
      this.error = 'Kullanıcı listesi alınamadı.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private fail(e: unknown, fallback: string): void {
    showToast(e instanceof ApiError && e.message ? e.message : fallback, 'warning');
  }

  private wireActs(): void {
    this.onAct('click', {
      openCreate: () => {
        this.createForm = this.emptyForm();
        this.createOpen = true;
        this.render();
      },
      closeCreate: () => {
        this.createOpen = false;
        this.render();
      },
      genPw: () => {
        this.createForm.password = generatePassword();
        this.render();
      },
      pickRole: (el) => {
        this.createForm.role = (el.dataset['role'] ?? 'operator') as Role;
        this.render();
      },
      submitCreate: () => void this.submitCreate(),

      roleMenu: (el) => {
        const u = el.dataset['user'] ?? null;
        this.roleMenuFor = this.roleMenuFor === u ? null : u;
        this.render();
      },
      setRole: (el) => void this.setRole(el.dataset['user'] ?? '', (el.dataset['role'] ?? '') as Role),

      openPw: (el) => {
        this.pwTarget = el.dataset['user'] ?? null;
        this.pwValue = generatePassword();
        this.pwDone = false;
        this.render();
      },
      closePw: () => {
        this.pwTarget = null;
        this.render();
      },
      genPw2: () => {
        this.pwValue = generatePassword();
        this.render();
      },
      submitPw: () => void this.submitPw(),
      copyPw: () => {
        void navigator.clipboard?.writeText(this.pwValue).then(
          () => showToast('Parola panoya kopyalandı'),
          () => showToast('Kopyalanamadı — elle seç', 'warning'),
        );
      },

      removeUser: (el) => void this.removeUser(el.dataset['user'] ?? ''),
      noop: (_el, ev) => ev.stopPropagation(),
    });

    this.onAct('input', {
      fUser: (el) => (this.createForm.username = (el as HTMLInputElement).value),
      fName: (el) => (this.createForm.fullName = (el as HTMLInputElement).value),
      fMail: (el) => (this.createForm.email = (el as HTMLInputElement).value),
      fPw: (el) => (this.createForm.password = (el as HTMLInputElement).value),
      pwVal: (el) => (this.pwValue = (el as HTMLInputElement).value),
    });
  }

  private async submitCreate(): Promise<void> {
    const f = this.createForm;
    const username = f.username.trim().toLowerCase();
    if (!/^[a-z0-9._-]{3,32}$/.test(username)) {
      showToast('Kullanıcı adı: 3-32 karakter, küçük harf/rakam/._-', 'warning');
      return;
    }
    if (f.password.length < 6) {
      showToast('Parola en az 6 karakter olmalı', 'warning');
      return;
    }
    try {
      await userService.create({
        username,
        password: f.password,
        role: f.role,
        fullName: f.fullName.trim() || undefined,
        email: f.email.trim() || undefined,
      });
      this.createOpen = false;
      showToast(`"${username}" eklendi (${ROLE_LABELS[f.role]})`);
      void this.load();
    } catch (e) {
      this.fail(e, 'Kullanıcı eklenemedi');
    }
  }

  private async setRole(username: string, role: Role): Promise<void> {
    if (!username || !role) return;
    this.roleMenuFor = null;
    try {
      await userService.setRole(username, role);
      showToast(`"${username}" rolü ${ROLE_LABELS[role]} yapıldı`);
      void this.load();
    } catch (e) {
      this.fail(e, 'Rol değiştirilemedi');
      this.render();
    }
  }

  private async submitPw(): Promise<void> {
    if (!this.pwTarget) return;
    if (this.pwValue.length < 6) {
      showToast('Parola en az 6 karakter olmalı', 'warning');
      return;
    }
    try {
      await userService.resetPassword(this.pwTarget, this.pwValue);
      this.pwDone = true;
      this.render();
    } catch (e) {
      this.fail(e, 'Şifre sıfırlanamadı');
    }
  }

  private async removeUser(username: string): Promise<void> {
    if (!username) return;
    const ok = await confirmDialog({
      title: 'Kullanıcı silinsin mi?',
      message: `"${username}" hesabı kalıcı olarak silinecek ve tüm oturumları kapatılacak.`,
      tone: 'danger',
      icon: 'ti ti-user-x',
      okLabel: 'Sil',
    });
    if (!ok) return;
    try {
      await userService.remove(username);
      showToast(`"${username}" silindi`);
      void this.load();
    } catch (e) {
      this.fail(e, 'Silinemedi');
    }
  }

  /* ---------------- template ---------------- */

  protected override template(): SafeHtml {
    return html`
      <div class="scr">
        <div style="flex:none;background:var(--vx-surface);border-bottom:1px solid var(--vx-border);padding:16px 16px 14px">
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
            <div>
              <div style="font:600 21px var(--vx-font-sans)">Kullanıcı & rol yönetimi</div>
              <div style="font:400 12.5px var(--vx-font-sans);color:var(--vx-text-2);margin-top:2px">Ekle, rol ata, şifre sıfırla · rol bazlı yetki matrisi</div>
            </div>
            <button class="btn pri sm" style="margin-left:auto" data-act="openCreate"><i class="ti ti-user-plus"></i>Yeni kullanıcı</button>
          </div>
        </div>
        ${this.bodyTemplate()}
        ${when(this.createOpen, () => this.createDialog())}
        ${when(this.pwTarget, () => this.pwDialog())}
      </div>`;
  }

  private bodyTemplate(): SafeHtml {
    if (this.loading) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center">
        <i class="ti ti-loader-2 spin" style="font-size:26px;color:var(--vx-primary)"></i></div>`;
    }
    if (this.error) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
        <div style="text-align:center;color:var(--vx-text-2)">
          <i class="ti ti-plug-connected-x" style="font-size:30px;color:var(--vx-text-3)"></i>
          <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">${this.error}</div>
        </div></div>`;
    }
    const me = session.get().user?.username;
    return html`
      <div class="body" style="padding:16px;display:flex;flex-wrap:wrap;gap:16px;align-content:flex-start;overflow-x:hidden">
        <div class="card" style="flex:1 1 340px;padding:8px">
          <p class="sect" style="padding:8px 8px 4px;margin:0">Kullanıcılar</p>
          ${each(this.list, (u) => this.userRow(u, u.username === me))}
        </div>

        <div class="card" style="flex:1 1 340px">
          <p class="sect">Yetki matrisi</p>
          <div style="display:flex;flex-direction:column;gap:2px">
            <div style="display:grid;grid-template-columns:1fr 58px 58px 58px;gap:6px;padding:0 0 8px;font:600 10px var(--vx-font-sans);text-transform:uppercase;letter-spacing:.03em;color:var(--vx-text-3)">
              <span>Yetki</span><span style="text-align:center">admin</span><span style="text-align:center">oper.</span><span style="text-align:center">izle.</span>
            </div>
            ${each(MATRIX, ([label, a, o, v]) => html`
              <div style="display:grid;grid-template-columns:1fr 58px 58px 58px;gap:6px;padding:8px 0;border-top:1px solid var(--vx-border);align-items:center">
                <span style="font:400 12.5px var(--vx-font-sans);color:var(--vx-text-2)">${label}</span>
                ${each([a, o, v], (allowed) => html`
                  <span style="text-align:center">
                    <i class="${allowed ? 'ti ti-circle-check' : 'ti ti-minus'}"
                       style="font-size:16px;color:${allowed ? 'var(--vx-success)' : 'var(--vx-text-3)'}"></i>
                  </span>`)}
              </div>`)}
          </div>
        </div>
      </div>`;
  }

  private userRow(u: User, isMe: boolean): SafeHtml {
    const chip = ROLE_CHIP[u.role];
    const display = u.fullName || u.username;
    const iconBtn =
      'width:32px;height:32px;border-radius:var(--vx-radius-md);border:1px solid var(--vx-border);background:var(--vx-surface);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--vx-text-2);flex:none';
    return html`
      <div style="position:relative">
        <div class="mrow" style="cursor:default">
          <span style="width:34px;height:34px;border-radius:50%;background:var(--vx-brand-50);color:var(--vx-brand-700);display:flex;align-items:center;justify-content:center;font:600 12px var(--vx-font-sans);flex:none">${initials(display)}</span>
          <span style="flex:1;min-width:0">
            <span style="display:block;font:600 13.5px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${display}${when(isMe, () => html`<span style="font:400 11px var(--vx-font-sans);color:var(--vx-text-3)"> (sen)</span>`)}</span>
            <span style="font:400 11px var(--vx-font-sans);color:var(--vx-text-3)">${u.email ?? u.username}</span>
          </span>
          <button class="chip" data-act="roleMenu" data-user="${u.username}" title="Rol değiştir"
                  style="background:${chip.bg};color:${chip.fg};font-weight:600;border:none;cursor:pointer">
            ${ROLE_LABELS[u.role]}<i class="ti ti-chevron-down" style="font-size:12px"></i>
          </button>
          <button data-act="openPw" data-user="${u.username}" title="Şifre sıfırla" style="${iconBtn}">
            <i class="ti ti-key" style="font-size:16px"></i>
          </button>
          ${when(!isMe, () => html`
            <button data-act="removeUser" data-user="${u.username}" title="Sil" style="${iconBtn};color:var(--vx-danger-text)">
              <i class="ti ti-trash" style="font-size:16px"></i>
            </button>`)}
        </div>
        ${when(this.roleMenuFor === u.username, () => html`
          <div style="position:absolute;right:8px;top:52px;z-index:20;background:var(--vx-surface);border:1px solid var(--vx-border);border-radius:var(--vx-radius-md);box-shadow:var(--vx-shadow-lg);padding:6px;min-width:230px">
            ${each(ROLES, (r) => html`
              <button class="mrow" style="padding:9px 10px" data-act="setRole" data-user="${u.username}" data-role="${r}">
                <i class="${ROLE_ICONS[r]}" style="font-size:16px;color:${r === u.role ? 'var(--vx-primary)' : 'var(--vx-text-3)'}"></i>
                <span style="flex:1;min-width:0">
                  <span style="display:block;font:600 12.5px var(--vx-font-sans)">${ROLE_LABELS[r]}</span>
                  <span style="font:400 10.5px var(--vx-font-sans);color:var(--vx-text-3)">${ROLE_DESCRIPTIONS[r]}</span>
                </span>
                ${when(r === u.role, () => html`<i class="ti ti-check" style="font-size:15px;color:var(--vx-primary)"></i>`)}
              </button>`)}
          </div>`)}
      </div>`;
  }

  private createDialog(): SafeHtml {
    const f = this.createForm;
    return html`
      <div class="ovl menu" data-act="closeCreate" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop" style="max-height:92%;display:flex;flex-direction:column">
          <div style="padding:17px 20px;border-bottom:1px solid var(--vx-border);display:flex;align-items:center;gap:9px;flex:none">
            <i class="ti ti-user-plus" style="font-size:20px;color:var(--vx-primary)"></i>
            <span style="font:600 17px var(--vx-font-sans)">Yeni kullanıcı</span>
          </div>
          <div class="body" style="padding:20px;display:flex;flex-direction:column;gap:13px">
            <div style="display:flex;gap:10px;flex-wrap:wrap">
              <div style="flex:1 1 150px"><label class="lbl">Kullanıcı adı</label>
                <input class="inp mono" data-act="fUser" placeholder="ali.veli" value="${f.username}" autocapitalize="off"></div>
              <div style="flex:1 1 150px"><label class="lbl">Ad soyad (ops.)</label>
                <input class="inp" data-act="fName" placeholder="Ali Veli" value="${f.fullName}"></div>
            </div>
            <div><label class="lbl">E-posta (ops.)</label>
              <input class="inp" data-act="fMail" placeholder="ali.veli@smg.com.tr" value="${f.email}"></div>
            <div>
              <label class="lbl">Rol</label>
              <div style="display:flex;gap:8px;flex-wrap:wrap">
                ${each(ROLES, (r) => html`
                  <button class="fchip ${f.role === r ? 'on' : ''}" data-act="pickRole" data-role="${r}">
                    <i class="${ROLE_ICONS[r]}" style="font-size:14px"></i>${ROLE_LABELS[r]}
                  </button>`)}
              </div>
              <div style="font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3);margin-top:6px">${ROLE_DESCRIPTIONS[f.role]}</div>
            </div>
            <div>
              <label class="lbl">Parola</label>
              <div style="display:flex;gap:8px">
                <input class="inp mono" data-act="fPw" value="${f.password}" style="flex:1">
                <button class="btn sec sm" data-act="genPw" title="Yeni üret" style="height:42px"><i class="ti ti-refresh"></i>Üret</button>
              </div>
              <div style="font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3);margin-top:6px">Bu parolayı kullanıcıya güvenli bir yolla ilet — ilk girişte kendisi değiştirebilir.</div>
            </div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:14px 20px;border-top:1px solid var(--vx-border);flex:none">
            <button class="btn sec" data-act="closeCreate">İptal</button>
            <button class="btn pri" data-act="submitCreate"><i class="ti ti-user-plus"></i>Ekle</button>
          </div>
        </div>
      </div>`;
  }

  private pwDialog(): SafeHtml {
    const target = this.pwTarget!;
    if (this.pwDone) {
      return html`
        <div class="ovl menu" style="animation:fadeIn .15s">
          <div class="sheet" data-act="noop">
            <div style="padding:26px 22px;text-align:center">
              <span style="width:54px;height:54px;border-radius:50%;background:var(--vx-success-bg);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px">
                <i class="ti ti-key" style="font-size:26px;color:var(--vx-success)"></i>
              </span>
              <div style="font:600 17px var(--vx-font-sans);margin-bottom:5px">Şifre sıfırlandı</div>
              <p style="font:400 13px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin:0 0 12px">
                "${target}" için yeni parola aşağıda — kullanıcıya güvenli bir yolla ilet.
                Eski oturumları kapatıldı.
              </p>
              <div class="mono" style="font:600 15px var(--vx-font-mono);background:var(--vx-surface-2);border:1px dashed var(--vx-border-strong);border-radius:var(--vx-radius-md);padding:11px;margin-bottom:14px;user-select:all">${this.pwValue}</div>
              <div style="display:flex;gap:10px;justify-content:center">
                <button class="btn sec" data-act="copyPw"><i class="ti ti-copy"></i>Kopyala</button>
                <button class="btn pri" data-act="closePw">Kapat</button>
              </div>
            </div>
          </div>
        </div>`;
    }
    return html`
      <div class="ovl menu" data-act="closePw" style="animation:fadeIn .15s">
        <div class="sheet" data-act="noop">
          <div style="padding:17px 20px;border-bottom:1px solid var(--vx-border);display:flex;align-items:center;gap:9px">
            <i class="ti ti-key" style="font-size:20px;color:var(--vx-primary)"></i>
            <span style="font:600 17px var(--vx-font-sans)">Şifre sıfırla — ${target}</span>
          </div>
          <div style="padding:20px">
            <label class="lbl">Yeni parola</label>
            <div style="display:flex;gap:8px">
              <input class="inp mono" data-act="pwVal" value="${this.pwValue}" style="flex:1">
              <button class="btn sec sm" data-act="genPw2" style="height:42px"><i class="ti ti-refresh"></i>Üret</button>
            </div>
            <div style="font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3);margin-top:6px">Sıfırlama sonrası kullanıcının tüm oturumları kapatılır.</div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:0 20px 20px">
            <button class="btn sec" data-act="closePw">İptal</button>
            <button class="btn pri" data-act="submitPw"><i class="ti ti-key"></i>Sıfırla</button>
          </div>
        </div>
      </div>`;
  }
}

customElements.define('smg-users-view', UsersView);
